<template>
  <div>
    <!--面包屑导航区域-->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>订单管理</el-breadcrumb-item>
      <el-breadcrumb-item>发卡活动池</el-breadcrumb-item>
    </el-breadcrumb>
    <!--卡片视图区-->
    <el-card>
      <!--搜索区域：flex 排布，同订单列表 order-filter-bar 模式-->
      <div class="order-filter-bar">
        <el-input
          class="order-filter-bar__item order-filter-bar__item--wide"
          placeholder="请输入活动标题"
          clearable
          v-model="queryInfo.title"
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        >
          <el-button
            slot="append"
            icon="el-icon-search"
            @click="handleSearch"
          ></el-button>
        </el-input>
        <el-select
          class="order-filter-bar__item order-filter-bar__item--md"
          v-model="queryInfo.storeId"
          placeholder="请选择活动门店"
          clearable
          filterable
          @change="handleSearch"
        >
          <el-option
            v-for="item in openStoreList"
            :key="item.id"
            :label="item.storename"
            :value="item.id"
          ></el-option>
        </el-select>
        <!-- default-time 让「有效期结束」默认落到当天 23:59:59：只选日期时结束默认是 00:00:00，
             等于查询终点被压到最后一天零点，最后一天才开始或结束的活动都会被漏掉 -->
        <el-date-picker
          class="order-filter-bar__item--date"
          v-model="queryInfo.dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="有效期开始"
          end-placeholder="有效期结束"
          value-format="yyyy-MM-dd HH:mm:ss"
          :picker-options="pickerOptions"
          :default-time="['00:00:00', '23:59:59']"
          @change="handleSearch"
        >
        </el-date-picker>
        <el-select
          class="order-filter-bar__item"
          v-model="queryInfo.status"
          placeholder="活动状态"
          clearable
          @change="handleSearch"
        >
          <el-option label="启用" :value="1"></el-option>
          <el-option label="停用" :value="0"></el-option>
        </el-select>
        <el-button
          class="order-filter-bar__reset"
          type="text"
          @click="resetQuery"
        >重置</el-button>
      </div>

      <!--操作区域：与筛选区分行。按钮降为 small，和表格 12px 的字号同一密度，
           不会比输入框（40px 高 / 14px 字）显得笨重-->
      <div class="order-action-bar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddDialog">创建活动</el-button>
        <el-button type="primary" size="small" plain @click="refreshAll">刷新</el-button>
        <span v-if="storeLoadFailed" class="store-load-tip">
          门店状态加载失败，发卡按钮可能不可用，请点击「刷新」重试
        </span>
      </div>

      <!--活动列表区域-->
      <!--列宽支持鼠标拖动调整：这是 el-table 自带能力，但表头的 mousedown/mousemove
           里都判断了 this.border，不开 border 拖拽完全不生效（列本身 resizable 默认已是 true）。
           border 带来的边框色已在样式里压浅，不影响整体观感 -->
      <el-table
        :data="list"
        class="card-grant-table"
        style="width: 100%"
        border
        stripe
        size="small"
        max-height="760"
        v-loading="loading"
      >
        <template slot="empty">
          <div class="empty-box">
            <i class="el-icon-tickets"></i>
            <p>{{ appliedFilter ? '没有符合条件的活动' : '还没有发卡活动' }}</p>
            <el-button
              v-if="appliedFilter"
              type="text"
              class="empty-box__action"
              @click="resetQuery"
            >清空筛选</el-button>
          </div>
        </template>
        <!-- 剩余宽度必须由多列一起分摊：el-table 会把容器剩余宽度平均分给所有
             min-width 列，只留一列吸收就会像之前那样单独涨到 470px，
             标题和门店之间空出一大片。所以标题/门店/有效期/创建时间这 4 列都给
             min-width、一起分摊；状态和操作内容定长，直接给 width 不参与分配 -->
        <el-table-column label="活动标题" min-width="160">
          <template slot-scope="scope">
            <span class="cell-title">{{ scope.row.title }}</span>
          </template>
        </el-table-column>
        <el-table-column label="活动门店" min-width="150">
          <template slot-scope="scope">
            <span class="cell-sub">{{ scope.row.storeName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="活动有效期" min-width="160">
          <template slot-scope="scope">
            <!-- 有效性不单开一列：它是这两行时间 + 当前时间的派生结果，
                 直接在时间文字上做深浅表达，过期/未开始整块压成浅灰 -->
            <div class="period-cell" :class="{ 'is-invalid': !isPeriodValid(scope.row) }">
              <div class="period-cell__row">{{ formatShortTime(scope.row.startTime) }}</div>
              <div class="period-cell__row period-cell__row--end">
                <span class="period-cell__sep">至</span>{{ formatShortTime(scope.row.endTime) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="95" align="center">
          <template slot-scope="scope">
            <!-- 只表达活动本身是否启用，和有效期无关 -->
            <el-tag v-if="scope.row.status === 1" type="success" size="mini">启用</el-tag>
            <el-tag v-else type="info" size="mini">停用</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="150">
          <template slot-scope="scope">
            <span class="cell-sub">{{ scope.row.createTime }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240">
          <template slot-scope="scope">
            <!-- 一列里只留一个实心按钮（发卡是本页主操作），其余降为文字按钮，
                 避免四五个彩色实心按钮并排、颜色互相打架 -->
            <div class="op-cell">
              <el-tooltip
                :content="issueDisabledReason(scope.row)"
                placement="top"
                :disabled="canIssue(scope.row)"
                :enterable="false"
                :append-to-body="true"
              >
                <span class="issue-btn-wrap">
                  <el-button
                    type="primary"
                    size="mini"
                    :disabled="!canIssue(scope.row)"
                    @click="handleIssue(scope.row)"
                  >发 卡</el-button>
                </span>
              </el-tooltip>
              <el-button
                type="text"
                size="mini"
                @click="showEditDialog(scope.row)"
              >编辑</el-button>
              <el-button
                type="text"
                size="mini"
                @click="goRecords(scope.row)"
              >记录</el-button>
              <el-button
                type="text"
                size="mini"
                @click="handleToggleStatus(scope.row)"
              >{{ scope.row.status === 1 ? '停用' : '启用' }}</el-button>
              <el-button
                type="text"
                size="mini"
                class="btn-text-danger"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div class="pager-wrap" align="center">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryInfo.pageNum"
          :page-sizes="[20, 50, 100]"
          :page-size="queryInfo.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        >
        </el-pagination>
      </div>
    </el-card>

    <!--创建/编辑活动对话框-->
    <!-- append-to-body 会把弹窗移到 body 下，弹窗外壳（头部分隔线、标题字号、底部按钮区）
         的样式 scoped 选不中 .el-dialog，统一放在文件末尾不带 scoped 的 style 里，
         用 .activity-dialog 圈住；表单内部的 form-tip 仍是 scoped，不受影响 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      custom-class="activity-dialog"
      append-to-body
      :show-close="!submitting"
      :close-on-click-modal="!submitting"
      :close-on-press-escape="!submitting"
      :before-close="handleDialogBeforeClose"
    >
      <el-form :model="form" :rules="formRules" ref="activityForm" label-width="100px">
        <el-form-item label="活动标题" prop="title">
          <!-- 不挂 show-word-limit：100 字上限对标题没有实际意义，右下角常驻一个 "0/100"
               反而是持续干扰；maxlength 仍然硬限制 -->
          <el-input
            v-model="form.title"
            placeholder="请输入活动标题"
            maxlength="100"
            clearable
            @input="clearFieldError('title')"
          ></el-input>
        </el-form-item>
        <el-form-item label="活动门店" prop="storeId">
          <!-- 编辑态门店只读：活动创建即与门店绑定，不可更换 -->
          <el-select
            v-if="!isEdit"
            v-model="form.storeId"
            placeholder="请选择门店"
            filterable
            :loading="storeLoading"
            style="width: 100%"
            @change="clearFieldError('storeId')"
          >
            <!-- 下拉只给营业中的门店：停业的创建活动也没有意义，发卡时一样会被拦。
                 原来是"列出来加(已停业)标注"，用户反馈后改为直接过滤 -->
            <el-option
              v-for="item in openStoreList"
              :key="item.id"
              :label="item.storename"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-input v-else :value="form.storeName" disabled></el-input>
          <div v-if="!isEdit && storeLoadFailed" class="form-tip">
            门店列表加载失败，请
            <el-button
              type="text"
              class="form-tip__retry"
              :loading="storeLoading"
              @click="loadStoreList"
            >重新加载</el-button>
          </div>
          <div v-if="isEdit" class="form-tip">活动与门店绑定，不可更换</div>
        </el-form-item>
        <el-form-item label="活动有效期" prop="dateRange">
          <!-- default-time 让「失效时间」默认落到当天 23:59:59：不然只选日期时结束是 00:00:00，
               最后一天等于还没开始就结束了；unlink-panels 让左右面板各自翻月，跨月选不用来回点 -->
          <el-date-picker
            v-model="form.dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="生效时间"
            end-placeholder="失效时间"
            :default-time="['00:00:00', '23:59:59']"
            unlink-panels
            value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="pickerOptions"
            style="width: 100%"
            @change="clearFieldError('dateRange')"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="状态" v-if="!isEdit">
          <!-- 两态单选，与门店/设备类型等字段保持同一种控件（项目里都是 el-radio） -->
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="submitting" @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCardGrantPeriodState } from '../../utils/cardGrantTime'

export default {
  name: 'ActivityPool',

  data() {
    return {
      queryInfo: {
        title: '',
        storeId: null,
        status: null,
        dateRange: [],
        pageNum: 1,
        pageSize: 20
      },
      list: [],
      storeList: [],
      // 门店下拉是异步拉的，数据没回来前 el-select 的 loading 会显示"加载中"
      storeLoading: false,
      // 门店状态拉取失败时给出持续提示，避免只弹一次 toast 后按钮一直灰着没人知道原因
      storeLoadFailed: false,
      total: 0,
      appliedFilter: false,
      loading: false,
      listRequestSeq: 0,
      submitting: false,
      dialogVisible: false,
      dialogTitle: '创建活动',
      isEdit: false,
      // 时间选择快捷方式，与订单列表一致
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      },
      form: {
        id: null,
        title: '',
        storeId: null,
        storeName: '',
        dateRange: [],
        status: 1,
        version: null
      },
      // 必填校验只在点「确定」时跑。Element 的 getFilteredRule 里有一句
      // `if (!rule.trigger || trigger === '') return true`，而 form.validate() 传的正是空串——
      // 所以不写 trigger 会变成 blur/change 也校验；只有给一个 Controller 不会发出的值
      // （这里用 submit）才能把校验严格收在提交那一刻
      formRules: {
        title: [{ required: true, message: '活动标题不能为空', trigger: 'submit' }],
        storeId: [{ required: true, message: '请选择活动门店', trigger: 'submit' }],
        dateRange: [{ required: true, message: '请选择活动有效期', trigger: 'submit' }]
      }
    }
  },

  computed: {
    // 两个门店下拉（筛选 + 创建活动）只给营业中的门店：停业的建活动没有意义，发卡时一样被拦。
    // storeList 保持全量——列表行的发卡禁用判断和原因提示（issueDisabledReason）要靠它认出停业门店
    openStoreList() {
      return this.storeList.filter((item) => !this.isStoreClosed(item))
    }
  },

  created() {
    this.loadStoreList()
    this.getList()
  },

  beforeDestroy() {
    // 页面离开后丢弃仍在途的列表响应，避免回包再改已销毁组件的数据
    this.listRequestSeq++
  },

  methods: {
    // 活动列表
    async getList() {
      const requestId = ++this.listRequestSeq
      const title = (this.queryInfo.title || '').trim()
      const range = this.queryInfo.dateRange || []
      // 空态文案按“实际提交的条件”判断，而不是输入框里刚敲到一半的值
      this.appliedFilter = !!(
        title ||
        this.queryInfo.storeId ||
        this.queryInfo.status !== null ||
        range.length === 2
      )
      this.loading = true
      try {
        const params = {
          title: title || undefined,
          storeId: this.queryInfo.storeId || undefined,
          status: this.queryInfo.status === null ? undefined : this.queryInfo.status,
          page: this.queryInfo.pageNum,
          limit: this.queryInfo.pageSize
        }
        // 有效期筛选：活动开始/结束时间落在查询区间内，或活动把整个查询区间包住，都算命中
        // （合起来等价于「活动与查询区间有交集」，详见后端 Filter_Where 注释）
        if (range.length === 2) {
          params.startTime = range[0]
          params.endTime = range[1]
        }
        const { data: res } = await this.$http.get('cardgrant/activity/list', { params })
        // 快速切换筛选时只认最后一次请求，旧响应不能覆盖新结果
        if (requestId !== this.listRequestSeq) return
        if (res.status === 0) {
          this.list = res.list || []
          this.total = res.total || 0
          // 删除/启停后当前页可能已超出总页数，回退到最后一页重查
          const maxPage = Math.max(1, Math.ceil(this.total / this.queryInfo.pageSize))
          if (this.queryInfo.pageNum > maxPage) {
            this.queryInfo.pageNum = maxPage
            // 递归请求会开启新的 requestId；外层 finally 不会再提前结束 loading
            return this.getList()
          }
        } else {
          this.$message.error(res.message || '查询失败')
        }
      } catch (error) {
        if (requestId !== this.listRequestSeq) return
        console.error('查询活动池失败：', error)
        this.$message.error('查询失败：' + (error.message || '未知错误'))
      } finally {
        if (requestId === this.listRequestSeq) this.loading = false
      }
    },
    // 刷新活动列表和门店状态：门店营业状态会变化，不能只刷活动列表
    refreshAll() {
      this.loadStoreList()
      this.getList()
    },
    // 门店下拉（该接口是 ResponseResult 套壳，取 res.data）
    async loadStoreList() {
      this.storeLoading = true
      try {
        const { data: res } = await this.$http.get('cardgrant/stores')
        if (res.status === 0) {
          this.storeList = res.data || []
          this.storeLoadFailed = false
        } else {
          this.storeLoadFailed = true
          this.$message.error(res.message || '门店列表加载失败，请点击刷新重试')
        }
      } catch (error) {
        console.error('查询门店下拉失败：', error)
        this.storeLoadFailed = true
        this.$message.error('门店列表加载失败，请点击刷新重试')
      } finally {
        this.storeLoading = false
      }
    },
    // 筛选条件变化：页码回到第一页，避免在第 2 页改条件后查到空页
    handleSearch() {
      this.queryInfo.pageNum = 1
      this.getList()
    },
    // 清空全部筛选条件并回到第一页（不逐项触发 @change，统一查一次）
    resetQuery() {
      this.queryInfo.title = ''
      this.queryInfo.storeId = null
      this.queryInfo.status = null
      this.queryInfo.dateRange = []
      this.queryInfo.pageNum = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.queryInfo.pageSize = val
      this.queryInfo.pageNum = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryInfo.pageNum = val
      this.getList()
    },
    // 启停活动：成功/失败都重查，保证状态筛选和总数与库一致
    async handleToggleStatus(row) {
      const target = row.status === 1 ? 0 : 1
      const actionText = target === 1 ? '启用' : '停用'
      try {
        const { data: res } = await this.$http.put(`cardgrant/activity/${row.id}/status`, null, {
          params: { status: target }
        })
        if (res.status === 0) {
          row.status = target
          this.$message.success(res.message || `已${actionText}`)
        } else {
          this.$message.error(res.message || `${actionText}失败`)
        }
        this.getList()
      } catch (error) {
        console.error('启停活动失败：', error)
        this.$message.error(`${actionText}失败：` + (error.message || '未知错误'))
        this.getList()
      }
    },
    showAddDialog() {
      if (this.submitting) return
      this.isEdit = false
      this.dialogTitle = '创建活动'
      this.form = {
        id: null,
        title: '',
        storeId: null,
        storeName: '',
        dateRange: [],
        status: 1,
        version: null
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.activityForm) this.$refs.activityForm.clearValidate()
      })
    },
    showEditDialog(row) {
      if (this.submitting) return
      this.isEdit = true
      this.dialogTitle = '编辑活动'
      this.form = {
        id: row.id,
        title: row.title,
        storeId: row.storeId,
        storeName: row.storeName,
        dateRange: [row.startTime, row.endTime],
        status: row.status,
        version: row.version
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.activityForm) this.$refs.activityForm.clearValidate()
      })
    },
    // 校验只在点「确定」时跑（见 formRules 的 trigger），所以用户一动字段就得手动把
    // 上一次留下的红字清掉，否则会出现"已经填了还标红"；下次点确定会重新校验
    clearFieldError(prop) {
      const form = this.$refs.activityForm
      if (form) form.clearValidate(prop)
    },
    // 提交中拦截 X、遮罩、ESC 的关闭动作；取消按钮另由 submitting 禁用
    handleDialogBeforeClose(done) {
      if (this.submitting) return
      done()
    },
    submitForm() {
      if (this.submitting) return
      this.$refs.activityForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            title: this.form.title,
            storeId: this.form.storeId,
            startTime: this.form.dateRange[0],
            endTime: this.form.dateRange[1]
          }
          if (this.isEdit) {
            // 带上打开弹窗时列表返回的版本号，后端做乐观锁校验
            payload.version = this.form.version
            const { data: res } = await this.$http.put(`cardgrant/activity/${this.form.id}`, payload)
            if (res.status === 0) {
              this.$message.success(res.message || '更新成功')
              this.dialogVisible = false
              this.getList()
            } else {
              this.$message.error(res.message || '更新失败')
              // 活动已被他人改过：关窗并刷新，避免继续基于旧数据编辑
              if (res.conflict) {
                this.dialogVisible = false
                this.getList()
              }
            }
          } else {
            payload.status = this.form.status
            const { data: res } = await this.$http.post('cardgrant/activity', payload)
            if (res.status === 0) {
              this.$message.success(res.message || '创建成功')
              this.dialogVisible = false
              this.getList()
            } else {
              this.$message.error(res.message || '创建失败')
            }
          }
        } catch (error) {
          console.error('保存活动失败：', error)
          this.$message.error('保存失败：' + (error.message || '未知错误'))
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`确定删除活动「${row.title}」吗？`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          const { data: res } = await this.$http.delete(`cardgrant/activity/${row.id}`)
          if (res.status === 0) {
            this.$message.success(res.message || '删除成功')
            this.getList()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (error) {
          console.error('删除活动失败：', error)
          this.$message.error('删除失败：' + (error.message || '未知错误'))
        }
      }).catch(() => {})
    },
    // 进入发卡记录页
    goRecords(row) {
      this.$router.push('/card-grant-activity-records/' + row.id)
    },
    // 直接发卡：用 sessionStorage 带过标志，记录页加载活动后自动弹出发卡框
    // （不用 query 传参，避免 router-view 的 fullPath key 变化导致组件重建、弹窗被销毁）
    handleIssue(row) {
      sessionStorage.setItem('cardGrantAutoIssue', String(row.id))
      this.$router.push('/card-grant-activity-records/' + row.id)
    },
    // 可发卡：与后端同口径（启用 + 正处于有效期内 + 门店未停业）
    canIssue(row) {
      return (
        !!row &&
        row.status === 1 &&
        !this.isStoreClosedById(row.storeId) &&
        this.getPeriodState(row) === 'running'
      )
    },
    // 禁用发卡时给出明确原因，避免只看到灰按钮不知道为什么不能点
    issueDisabledReason(row) {
      if (!row) return ''
      if (row.status !== 1) return '该活动已停用，无法发卡'
      if (this.isStoreClosedById(row.storeId)) {
        if (this.storeLoadFailed) return '门店状态加载失败，请点击「刷新」重试'
        const store = this.storeList.find((item) => String(item.id) === String(row.storeId))
        return store ? '该门店已停业，无法发卡' : '该门店不存在或已删除，无法发卡'
      }
      const state = this.getPeriodState(row)
      if (state === 'notStart') return '该活动尚未开始，无法发卡'
      if (state === 'ended') return '该活动已结束，无法发卡'
      if (state !== 'running') return '该活动有效期不完整，无法发卡'
      return ''
    },
    // 只有 '1' 是营业；'0'、'2'、空值以及门店列表未加载/查不到都按停业处理
    isStoreClosed(store) {
      return !store || String(store.isopen) !== '1'
    },
    isStoreClosedById(storeId) {
      const store = this.storeList.find((item) => String(item.id) === String(storeId))
      return this.isStoreClosed(store)
    },
    // 有效期状态：notStart 未开始 / running 进行中 / ended 已结束
    getPeriodState(row) {
      if (!row) return ''
      return getCardGrantPeriodState(row.startTime, row.endTime)
    },
    // 当前时间是否落在有效期内（未开始、已结束都算不在期内）。
    // 起止时间缺失时视为有效，不做置灰，避免把"没填时间"误画成"已失效"
    isPeriodValid(row) {
      const state = this.getPeriodState(row)
      return state === '' || state === 'running'
    },
    // 有效期只展示到分钟，"秒"在这个场景没有意义（都是 00 或 59），去掉能省 5 个字符
    formatShortTime(time) {
      if (!time) return '-'
      const str = String(time)
      return str.length >= 16 ? str.slice(0, 16) : str
    }
  }
}
</script>

<style scoped>
/* 面包屑默认被全局 style.css 设成 width:90% + 居中，会比卡片左缘内缩，
   这里按订单列表页的做法压回整宽，保证与卡片严格左对齐 */
.el-breadcrumb {
  width: 100%;
}
/* ===== 表格表头 ===== */
/* 列宽拖拽开了 border，默认边框色在浅色表格上偏重，统一压浅一档 */
.card-grant-table >>> .el-table__header-wrapper th,
.card-grant-table >>> .el-table__body-wrapper td {
  border-color: #ebeef5;
}
/* 拖拽是 Element 原生能力：border 模式下鼠标进到表头右缘 8px 内会变成 col-resize，
   按住左键左右拖即可改列宽（html 不需要额外绑定）。它本身没有可见手柄，
   这里把 hover 列的右分隔线提亮一档，让“这条边能拖”有个静态提示 */
.card-grant-table >>> .el-table__header-wrapper th:hover {
  border-right-color: #a0cfff;
}
/* 拖动时跟随鼠标的竖直参考线。Element 默认给它 #ebeef5（和边框同色），
   压在浅灰表头上几乎看不见，提成主色才看得出边界拖到哪了 */
.card-grant-table >>> .el-table__column-resize-proxy {
  border-left-color: #409eff;
}
/* Element 默认表头是白底 + 12px 浅灰字，和白色表体几乎分不开。
   改成浅灰底 + 深色半粗字 + 加高，把“字段名”和“数据”在视觉上分成两层 */
.card-grant-table >>> .el-table__header-wrapper th {
  height: 44px;
  background-color: #f5f7fa;
  color: #303133;
  font-weight: 600;
  border-bottom: 1px solid #e4e7ed;
}
/* 表体行也加高 1px，表头变重之后行距不至于显得发挤 */
.card-grant-table >>> .el-table__body-wrapper td {
  padding-top: 9px;
  padding-bottom: 9px;
}
/* 首列去掉左侧内边距，让表格首列文字与上方筛选控件、卡片内容左缘严格对齐 */
.card-grant-table >>> .el-table__header-wrapper th:first-child .cell,
.card-grant-table >>> .el-table__body-wrapper td:first-child .cell {
  padding-left: 0;
}
/* 门店状态加载失败提示：跟在操作按钮后面，避免整行被撑高 */
.store-load-tip {
  font-size: 12px;
  line-height: 20px;
  color: #f56c6c;
}
/* ===== 搜索域排版（同订单列表 order-filter-bar 模式）===== */
.order-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.order-filter-bar__item {
  /* 状态这类短下拉：固定宽，保证一行内各控件宽度节奏一致 */
  flex: 0 0 140px;
}
.order-filter-bar__item--md {
  /* 门店名较长，单独给一档宽度 */
  flex: 0 0 200px;
}
.order-filter-bar__item--wide {
  /* 活动标题：按实际标题长度取值（约 27 个汉字），不撑满整行 */
  flex: 0 0 390px;
}
.order-filter-bar__item--date {
  /* 日期范围独立档位，不参与伸缩（挂 __item 会被固定宽度覆盖）。
     400px 是 Element 给 datetimerange 的默认宽度：内部两个输入各占 39%，
     19 个字符（2026-10-23 14:00:00）在 14px 下约 130px，压到 340px 时「秒」会被截掉 */
  flex: 0 0 400px;
}
/* 重置是文字按钮（没有边框），与左侧下拉多留 4px，避免看起来贴着 */
.order-filter-bar__reset {
  flex: 0 0 auto;
  margin-left: 4px;
}
/* Element UI 2.x 的 el-input/el-select 根节点自带 width:100%，
   在 flex 容器里必须显式设置，否则宽度不受 flex 控制 */
.order-filter-bar >>> .el-input,
.order-filter-bar >>> .el-select {
  width: 100%;
}
/* 日期范围内部的输入框字号默认 13px，比 el-input 的 14px 小一号，
   放一排会明显不齐，这里统一到 14px */
.order-filter-bar >>> .el-range-input {
  font-size: 14px;
}
.order-action-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.order-action-bar .el-button {
  /* 覆盖 Element 默认的 margin-left:10px，统一由 gap 控制间距 */
  margin-left: 0 !important;
  margin-right: 0;
  flex-shrink: 0;
}
/* 活动标题是主信息，加重；门店名是次信息，压淡，形成两级层次 */
.cell-title {
  font-weight: 500;
  color: #303133;
}
.cell-sub {
  color: #606266;
}
/* 有效期列：开始 / 结束上下两行。tabular-nums 让数字等宽，
   两行的时间数字上下对得齐，不会因为 1 和 8 宽度不同而错位 */
.period-cell {
  font-size: 12px;
  line-height: 1.7;
  font-variant-numeric: tabular-nums;
}
.period-cell__row {
  color: #303133;
}
/* 结束时间比开始时间淡一档，“至”再淡一档，形成 3 级层次 */
.period-cell__row--end {
  color: #606266;
}
.period-cell__sep {
  color: #c0c4cc;
  margin-right: 4px;
}
/* 不在有效期内（未开始 / 已结束）：整块时间压成浅灰，扫一眼就能看出来 */
.period-cell.is-invalid .period-cell__row {
  color: #c0c4cc;
}
/* 操作列：按钮间距统一由 gap 控制，去掉 Element 默认的 margin-left */
.op-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.op-cell .el-button {
  margin-left: 0 !important;
  margin-right: 0;
  flex-shrink: 0;
}
/* 禁用按钮不触发鼠标事件，Tooltip 需要这层可接收 hover 的包装元素 */
.issue-btn-wrap {
  display: inline-flex;
  flex-shrink: 0;
}
/* 文字按钮自带的左右内边距为 0，再压掉上下内边距，让它与实心按钮视觉居中 */
.op-cell .el-button--text {
  padding-top: 0;
  padding-bottom: 0;
}
/* 删除是危险操作，用红色文字区分普通文字按钮 */
.btn-text-danger {
  color: #f56c6c;
}
.btn-text-danger:hover {
  color: #f78989;
}
/* 分页与表格之间用固定间距，替掉原来靠 <br> 撑开的做法 */
.pager-wrap {
  padding-top: 14px;
}
/* 空态：给一句可执行的提示，而不是干巴巴的“暂无数据” */
.empty-box {
  padding: 28px 0 20px;
  color: #909399;
}
.empty-box i {
  font-size: 32px;
  color: #dcdfe6;
  display: block;
  margin-bottom: 8px;
}
.empty-box p {
  margin: 0;
  font-size: 13px;
}
.empty-box__action {
  margin-top: 4px;
  padding: 0;
  font-size: 13px;
}
/* 编辑态「活动与门店绑定」这句副文案：与输入框拉开 6px，
   4px 时 12px 小字贴着控件底部会显挤 */
.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.form-tip__retry {
  padding: 0;
  font-size: 12px;
  vertical-align: baseline;
}
</style>

<!--弹窗外壳：append-to-body 之后弹窗在 body 下，scoped 选不中 .el-dialog，
    所以单独开一个不带 scoped 的块。所有规则都以 .activity-dialog 起头，
    只管这一个弹窗，不会漏到全局其它 dialog -->
<style>
/* 外壳轮廓：默认 2px 圆角 + 1px 硬投影，在浅灰页面上显得"糊"。
   选择器带上 .el-dialog 是为了压过主题里的 `.el-dialog`（同权重时只靠加载顺序不稳）。
   overflow:hidden 让圆角真正裁住头部浅灰底，否则四角会露出白边 */
.el-dialog.activity-dialog {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}
/* 极浅灰头部：比纯白多一层"面板"感，又不像订单页那种彩色头部那么抢眼 */
.activity-dialog .el-dialog__header {
  padding: 18px 24px 16px;
  background-color: #fafbfc;
  border-bottom: 1px solid #ebeef5;
}
.activity-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
/* ✕ 默认 20px 灰度太浅，压在右上角显得脏；按 18px 头部重新对中 */
.activity-dialog .el-dialog__headerbtn {
  top: 21px;
  right: 22px;
}
.activity-dialog .el-dialog__headerbtn .el-dialog__close {
  font-size: 17px;
  color: #c0c4cc;
}
.activity-dialog .el-dialog__headerbtn:hover .el-dialog__close {
  color: #409eff;
}
/* 底部只留 6px：最后一个表单项自带 20px 下边距，再叠加会空出一大块 */
.activity-dialog .el-dialog__body {
  padding: 22px 24px 6px;
}
/* 操作区和内容用一条线分开，避免按钮看着像浮在表单下面 */
.activity-dialog .el-dialog__footer {
  padding: 14px 24px 18px;
  border-top: 1px solid #ebeef5;
}
/* 四个字段在 600px 宽里按 20px 行距排，比默认 22px 紧凑一点，弹窗不至于被撑高 */
.activity-dialog .el-form-item {
  margin-bottom: 20px;
}
.activity-dialog .el-form-item__label {
  padding-right: 14px;
}
.activity-dialog .el-form-item__error {
  padding-top: 2px;
}
/* 日期区间里的两个输入框默认 13px，比 el-input 的 14px 小一号，单独拧齐 */
.activity-dialog .el-range-input {
  font-size: 14px;
}
/* 区间选择器自己的左右内边距是 10px，普通 el-input 是 15px —— 三行控件摞在一起时，
   日期框的文字比上面的标题、门店少缩进 5px，左边缘会错开 */
.activity-dialog .el-range-editor.el-input__inner {
  padding-left: 15px;
  padding-right: 15px;
}
/* 分隔符「至」默认用的是正文色，比两侧的 placeholder 还重，压淡一档退到背景里 */
.activity-dialog .el-range-separator {
  color: #c0c4cc;
}
</style>
